import { useState, useEffect } from 'react';
import { useI18n } from '../i18n';

interface ImageModalProps {
	isOpen: boolean;
	onClose: () => void;
	images: Array<{ url: string; alt: string; label: string }>;
	initialIndex?: number;
}

export default function ImageModal({ isOpen, onClose, images, initialIndex = 0 }: ImageModalProps) {
	const [currentIndex, setCurrentIndex] = useState(initialIndex);
	const { language } = useI18n();

	const copy = (en: string, de: string, hr: string) =>
		language === 'de' ? de : language === 'hr' ? hr : en;

	// Update current index when modal opens with a new initial index
	useEffect(() => {
		if (isOpen) {
			setCurrentIndex(initialIndex);
		}
	}, [isOpen, initialIndex]);

	// Handle keyboard navigation
	useEffect(() => {
		if (!isOpen) return;

		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === 'Escape') {
				onClose();
			} else if (e.key === 'ArrowLeft') {
				handlePrevious();
			} else if (e.key === 'ArrowRight') {
				handleNext();
			}
		};

		window.addEventListener('keydown', handleKeyDown);
		return () => window.removeEventListener('keydown', handleKeyDown);
	}, [isOpen, currentIndex]);

	if (!isOpen || images.length === 0) return null;

	const currentImage = images[currentIndex];
	const hasMultiple = images.length > 1;

	const handlePrevious = () => {
		setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
	};

	const handleNext = () => {
		setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
	};

	return (
		<div
			className="modal fade show"
			style={{
				display: 'block',
				backgroundColor: 'rgba(0, 0, 0, 0.9)',
				// Must be above app fixed headers to keep the close button visible.
				zIndex: 20000
			}}
			onClick={onClose}
			tabIndex={-1}
		>
			<div
				className="modal-dialog modal-fullscreen"
				onClick={(e) => e.stopPropagation()}
				style={{ zIndex: 20001 }}
			>
				<div className="modal-content bg-transparent border-0">
					<div
						className="modal-header border-0 bg-transparent position-absolute top-0 start-0 end-0"
						style={{ zIndex: 20002, position: 'fixed' }}
					>
						<div className="d-flex justify-content-between align-items-center w-100">
							<h5 className="text-white mb-0">
								{currentImage.label}
								{hasMultiple && (
									<span className="text-white-50 ms-2">
										({currentIndex + 1} / {images.length})
									</span>
								)}
							</h5>
							<button
								type="button"
								className="btn-close btn-close-white"
								onClick={onClose}
								style={{ pointerEvents: 'auto', zIndex: 20003, opacity: 1 }}
								aria-label={copy('Close', 'Schließen', 'Zatvori')}
							/>
						</div>
					</div>
					<div
						className="modal-body d-flex align-items-center justify-content-center position-relative"
						style={{ minHeight: '100vh' }}
						onClick={onClose}
					>
						{hasMultiple && (
							<button
								className="btn btn-lg text-white position-absolute start-0 top-50 translate-middle-y ms-3"
								style={{
									backgroundColor: 'rgba(0, 0, 0, 0.5)',
									border: 'none',
									borderRadius: '50%',
									width: '50px',
									height: '50px',
									zIndex: 20003
								}}
								onClick={(e) => {
									e.stopPropagation();
									handlePrevious();
								}}
								aria-label={copy('Previous image', 'Vorheriges Bild', 'Prethodna slika')}
							>
								<i className="bi bi-chevron-left"></i>
							</button>
						)}
						
						<div
							onClick={(e) => e.stopPropagation()}
							style={{
								width: 'min(92vw, 1400px)',
								height: 'min(82vh, 900px)',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center'
							}}
						>
							<img
								src={currentImage.url}
								alt={currentImage.alt}
								style={{
									width: '100%',
									height: '100%',
									objectFit: 'contain',
									cursor: 'pointer'
								}}
								onClick={(e) => {
									// Clicking on the photo advances to the next image (if available),
									// instead of closing the modal.
									e.stopPropagation();
									if (hasMultiple) handleNext();
								}}
							/>
						</div>
						
						{hasMultiple && (
							<button
								className="btn btn-lg text-white position-absolute end-0 top-50 translate-middle-y me-3"
								style={{
									backgroundColor: 'rgba(0, 0, 0, 0.5)',
									border: 'none',
									borderRadius: '50%',
									width: '50px',
									height: '50px',
									zIndex: 20003
								}}
								onClick={(e) => {
									e.stopPropagation();
									handleNext();
								}}
								aria-label={copy('Next image', 'Nächstes Bild', 'Sljedeća slika')}
							>
								<i className="bi bi-chevron-right"></i>
							</button>
						)}
					</div>
					{hasMultiple && (
						<div
							className="modal-footer border-0 bg-transparent position-absolute bottom-0 start-0 end-0"
							style={{ zIndex: 20002 }}
							onClick={(e) => e.stopPropagation()}
						>
							<div className="d-flex gap-2 justify-content-center w-100">
								{images.map((_, index) => (
									<button
										key={index}
										className={`btn btn-sm rounded-pill ${
											index === currentIndex ? 'btn-light' : 'btn-outline-light'
										}`}
										onClick={(e) => {
											e.stopPropagation();
											setCurrentIndex(index);
										}}
										aria-label={copy(`Go to image ${index + 1}`, `Zu Bild ${index + 1} wechseln`, `Idi na sliku ${index + 1}`)}
									>
										{index + 1}
									</button>
								))}
							</div>
						</div>
					)}
				</div>
			</div>
		</div>
	);
}

